import { BucketVencimientoBadge, FechaCR, MontoCRC, MontoUSD } from '@/components/dominio';
import { BankAccountPickerBC } from '@/components/filtros/BankAccountPickerBC';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCuentasBancoBC } from '@/hooks/useCuentasBancoBC';
import type { LineaCanasta } from '@/lib/PropuestaEnConstruccionContext';
import { lineaDesdeFactura } from '@/lib/montosPropuesta';
import { cn } from '@/lib/utils';
import type { VencimientoDetalle } from '@adelante/shared';
import {
  ArrowSquareOut,
  CaretDown,
  CaretRight,
  CaretUp,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import { Fragment, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

interface Props {
  /** Universo completo de facturas (se usa para mostrar TODAS las facturas de un proveedor al expandirlo, no solo las que cumplen un criterio). */
  facturas: VencimientoDetalle[];
  /**
   * Si se pasa, solo estos proveedores aparecen como fila (ej. los que cumplen el criterio en
   * "Nueva propuesta"). Si se omite, aparecen todos los proveedores presentes en `facturas`
   * (ej. en "Agregar facturas", donde se puede buscar cualquier proveedor).
   */
  vendorCodesVisibles?: Set<string>;
  /** Etiqueta junto al N.º de documento (p. ej. "Cerrada en BC", "Aplicado a otra factura"), por `entryNo`. */
  etiquetas?: Record<number, { texto: string; titulo?: string; tono: 'ambar' | 'rojo' }>;
  /** Líneas actualmente incluidas — subconjunto de `facturas` por `entryNo`. */
  value: LineaCanasta[];
  onChange: (lineas: LineaCanasta[]) => void;
}

type CampoOrdenProveedor = 'vendorName' | 'totalIncluidoLCY' | 'cantidadIncluidas';

const PORCENTAJES_RAPIDOS = [25, 33, 50, 100];

function formatearMonto(monto: number, currencyCode: string | null) {
  return currencyCode === 'USD' ? <MontoUSD monto={monto} /> : <MontoCRC monto={monto} />;
}

export function SelectorFacturasPorProveedor({
  facturas,
  vendorCodesVisibles,
  etiquetas,
  value,
  onChange,
}: Props) {
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState<{ campo: CampoOrdenProveedor; dir: 'asc' | 'desc' }>({
    campo: 'totalIncluidoLCY',
    dir: 'desc',
  });
  const { data: cuentasBancoData } = useCuentasBancoBC();
  const cuentasBanco = cuentasBancoData?.data ?? [];

  const incluidas = useMemo(() => new Map(value.map((l) => [l.entryNo, l])), [value]);

  const facturasPorVendor = useMemo(() => {
    const mapa = new Map<string, VencimientoDetalle[]>();
    for (const f of facturas) {
      const lista = mapa.get(f.vendorCode) ?? [];
      lista.push(f);
      mapa.set(f.vendorCode, lista);
    }
    return mapa;
  }, [facturas]);

  // El orden de un proveedor expandido se congela mientras se edita: si no, al cambiar un monto el proveedor
  // salta de lugar (se ordena por total) y se pierde de vista. Vuelve a ordenarse al contraerlo o al
  // cambiar el orden de la tabla.
  const valoresCongelados = useRef(new Map<string, Record<CampoOrdenProveedor, string | number>>());
  const ordenDeCongelados = useRef('');

  const grupos = useMemo(() => {
    const claveOrden = `${orden.campo}:${orden.dir}`;
    if (ordenDeCongelados.current !== claveOrden) {
      valoresCongelados.current.clear();
      ordenDeCongelados.current = claveOrden;
    }
    for (const codigo of [...valoresCongelados.current.keys()]) {
      if (!expandidos.has(codigo)) valoresCongelados.current.delete(codigo);
    }
    const vendorCodes = vendorCodesVisibles
      ? [...vendorCodesVisibles]
      : [...facturasPorVendor.keys()];
    const filas = vendorCodes.map((vendorCode) => {
      const facs = facturasPorVendor.get(vendorCode) ?? [];
      const monedas = new Set(facs.map((f) => f.currencyCode || 'CRC'));
      const incluidasDelProveedor = facs.filter((f) => incluidas.has(f.entryNo));
      const cuentasIncluidas = new Set(
        incluidasDelProveedor.map((f) => incluidas.get(f.entryNo)?.cuentaBancoOrigen ?? null),
      );
      return {
        vendorCode,
        vendorName: facs[0]?.vendorName ?? vendorCode,
        currencyCode: monedas.size === 1 ? ([...monedas][0] ?? null) : 'Mixto',
        facturas: facs,
        cantidadIncluidas: incluidasDelProveedor.length,
        cuentaComun: cuentasIncluidas.size === 1 ? ([...cuentasIncluidas][0] ?? null) : null,
        faltaCuenta: incluidasDelProveedor.some(
          (f) => !incluidas.get(f.entryNo)?.cuentaBancoOrigen,
        ),
        totalIncluido: incluidasDelProveedor.reduce(
          (acc, f) => acc + (incluidas.get(f.entryNo)?.montoAPagar ?? 0),
          0,
        ),
        totalIncluidoLCY: incluidasDelProveedor.reduce(
          (acc, f) => acc + (incluidas.get(f.entryNo)?.montoAPagarLCY ?? 0),
          0,
        ),
      };
    });
    for (const f of filas) {
      if (expandidos.has(f.vendorCode) && !valoresCongelados.current.has(f.vendorCode)) {
        valoresCongelados.current.set(f.vendorCode, {
          vendorName: f.vendorName,
          totalIncluidoLCY: f.totalIncluidoLCY,
          cantidadIncluidas: f.cantidadIncluidas,
        });
      }
    }
    const signo = orden.dir === 'asc' ? 1 : -1;
    return filas.sort((a, b) => {
      const va = valoresCongelados.current.get(a.vendorCode)?.[orden.campo] ?? a[orden.campo];
      const vb = valoresCongelados.current.get(b.vendorCode)?.[orden.campo] ?? b[orden.campo];
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * signo;
      return ((va as number) - (vb as number)) * signo;
    });
  }, [facturasPorVendor, vendorCodesVisibles, incluidas, orden, expandidos]);

  const gruposFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return grupos;
    return grupos.filter(
      (g) => g.vendorName.toLowerCase().includes(q) || g.vendorCode.toLowerCase().includes(q),
    );
  }, [grupos, busqueda]);

  const todasLasFacturasVisibles = useMemo(
    () => gruposFiltrados.flatMap((g) => g.facturas),
    [gruposFiltrados],
  );
  const todasIncluidasGlobal =
    todasLasFacturasVisibles.length > 0 &&
    todasLasFacturasVisibles.every((f) => incluidas.has(f.entryNo));
  const algunaIncluidaGlobal = todasLasFacturasVisibles.some((f) => incluidas.has(f.entryNo));

  function alternarOrden(campo: CampoOrdenProveedor) {
    setOrden((actual) =>
      actual.campo === campo
        ? { campo, dir: actual.dir === 'asc' ? 'desc' : 'asc' }
        : { campo, dir: 'desc' },
    );
  }

  function alternarExpandido(vendorCode: string) {
    setExpandidos((actual) => {
      const nuevo = new Set(actual);
      if (nuevo.has(vendorCode)) nuevo.delete(vendorCode);
      else nuevo.add(vendorCode);
      return nuevo;
    });
  }

  function alternarTodas() {
    if (todasIncluidasGlobal) {
      const entryNos = new Set(todasLasFacturasVisibles.map((f) => f.entryNo));
      onChange(value.filter((l) => !entryNos.has(l.entryNo)));
    } else {
      const existentes = new Set(value.map((l) => l.entryNo));
      const nuevas = todasLasFacturasVisibles
        .filter((f) => !existentes.has(f.entryNo))
        .map(lineaDesdeFactura);
      onChange([...value, ...nuevas]);
    }
  }

  function alternarFactura(f: VencimientoDetalle) {
    if (incluidas.has(f.entryNo)) {
      onChange(value.filter((l) => l.entryNo !== f.entryNo));
    } else {
      onChange([...value, lineaDesdeFactura(f)]);
    }
  }

  function alternarProveedor(facs: VencimientoDetalle[]) {
    const todasIncluidas = facs.every((f) => incluidas.has(f.entryNo));
    if (todasIncluidas) {
      const entryNos = new Set(facs.map((f) => f.entryNo));
      onChange(value.filter((l) => !entryNos.has(l.entryNo)));
    } else {
      const existentes = new Set(value.map((l) => l.entryNo));
      const nuevas = facs.filter((f) => !existentes.has(f.entryNo)).map(lineaDesdeFactura);
      onChange([...value, ...nuevas]);
    }
  }

  function fijarMonto(entryNo: number, monto: number) {
    const linea = incluidas.get(entryNo);
    if (!linea) return;
    let montoFinal = monto;
    if (!Number.isFinite(montoFinal) || montoFinal < 0) montoFinal = 0;
    if (montoFinal > linea.montoMaximo) {
      toast.warning('El monto no puede superar el saldo pendiente de la factura');
      montoFinal = linea.montoMaximo;
    }
    onChange(
      value.map((l) =>
        l.entryNo === entryNo
          ? { ...l, montoAPagar: montoFinal, montoAPagarLCY: montoFinal * l.tasaImplicita }
          : l,
      ),
    );
  }

  function aplicarCuentaVendor(vendorCode: string, cuentaBancoOrigen: string | null) {
    const cuenta = cuentasBanco.find((c) => c.number === cuentaBancoOrigen);
    const monedaCuenta = cuenta?.currencyCode || 'CRC';
    let saltadas = 0;
    onChange(
      value.map((l) => {
        if (l.vendorCode !== vendorCode) return l;
        if ((l.currencyCode || 'CRC') !== monedaCuenta) {
          saltadas++;
          return l;
        }
        return { ...l, cuentaBancoOrigen };
      }),
    );
    if (saltadas > 0) {
      toast.warning(
        `${saltadas} factura(s) de este proveedor están en otra moneda y no se asignaron (${monedaCuenta}).`,
      );
    }
  }

  function aplicarCuentaFactura(entryNo: number, cuentaBancoOrigen: string | null) {
    onChange(value.map((l) => (l.entryNo === entryNo ? { ...l, cuentaBancoOrigen } : l)));
  }

  function encabezadoOrdenable(etiqueta: string, campo: CampoOrdenProveedor) {
    const activo = orden.campo === campo;
    return (
      <button
        type="button"
        onClick={() => alternarOrden(campo)}
        className="flex items-center gap-1 hover:text-foreground"
      >
        {etiqueta}
        {activo &&
          (orden.dir === 'asc' ? (
            <CaretUp className="h-3 w-3" weight="bold" aria-hidden="true" />
          ) : (
            <CaretDown className="h-3 w-3" weight="bold" aria-hidden="true" />
          ))}
      </button>
    );
  }

  if (grupos.length === 0) {
    return <p className="text-sm text-muted-foreground">No hay facturas para mostrar.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="relative w-[280px]">
        <MagnifyingGlass
          className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
          weight="bold"
          aria-hidden="true"
        />
        <Input
          placeholder="Buscar proveedor..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="pl-8"
        />
      </div>
      {gruposFiltrados.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ningún proveedor coincide con "{busqueda}".</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  checked={todasIncluidasGlobal}
                  ref={(el) => {
                    if (el) el.indeterminate = algunaIncluidaGlobal && !todasIncluidasGlobal;
                  }}
                  onChange={alternarTodas}
                  title="Seleccionar todos los proveedores"
                  className="h-4 w-4 accent-adelante-primary"
                />
              </TableHead>
              <TableHead>Código</TableHead>
              <TableHead>{encabezadoOrdenable('Proveedor', 'vendorName')}</TableHead>
              <TableHead>Moneda</TableHead>
              <TableHead className="text-right">
                {encabezadoOrdenable('Facturas incluidas', 'cantidadIncluidas')}
              </TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">
                {encabezadoOrdenable('Total DL', 'totalIncluidoLCY')}
              </TableHead>
              <TableHead>Cuenta bancaria</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {gruposFiltrados.map((g) => {
              const expandido = expandidos.has(g.vendorCode);
              const todasIncluidas = g.facturas.every((f) => incluidas.has(f.entryNo));
              const algunaIncluida = g.cantidadIncluidas > 0;
              return (
                <Fragment key={g.vendorCode}>
                  <TableRow
                    className={cn(
                      'cursor-pointer border-b-2 border-b-adelante-primary/20 font-medium',
                      g.faltaCuenta ? 'bg-amber-50' : 'bg-muted/50',
                    )}
                  >
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={todasIncluidas}
                        ref={(el) => {
                          if (el) el.indeterminate = algunaIncluida && !todasIncluidas;
                        }}
                        onChange={() => alternarProveedor(g.facturas)}
                        className="h-4 w-4 accent-adelante-primary"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell
                      className="font-mono text-xs font-normal"
                      onClick={() => alternarExpandido(g.vendorCode)}
                    >
                      {g.vendorCode}
                    </TableCell>
                    <TableCell onClick={() => alternarExpandido(g.vendorCode)}>
                      <span className="flex items-center gap-1">
                        {expandido ? (
                          <CaretDown className="h-3 w-3 text-muted-foreground" weight="bold" />
                        ) : (
                          <CaretRight className="h-3 w-3 text-muted-foreground" weight="bold" />
                        )}
                        {g.vendorName}
                      </span>
                    </TableCell>
                    <TableCell
                      className="font-normal"
                      onClick={() => alternarExpandido(g.vendorCode)}
                    >
                      {g.currencyCode || 'CRC'}
                    </TableCell>
                    <TableCell
                      className="text-right font-normal tabular-nums"
                      onClick={() => alternarExpandido(g.vendorCode)}
                    >
                      {g.cantidadIncluidas} / {g.facturas.length}
                    </TableCell>
                    <TableCell
                      className="text-right font-normal"
                      onClick={() => alternarExpandido(g.vendorCode)}
                    >
                      {formatearMonto(g.totalIncluido, g.currencyCode)}
                    </TableCell>
                    <TableCell
                      className="text-right font-normal"
                      onClick={() => alternarExpandido(g.vendorCode)}
                    >
                      <MontoCRC monto={g.totalIncluidoLCY} />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <BankAccountPickerBC
                        value={g.cuentaComun}
                        onChange={(cuenta) => aplicarCuentaVendor(g.vendorCode, cuenta)}
                        soloMoneda={
                          g.currencyCode === 'Mixto' ? undefined : g.currencyCode || 'CRC'
                        }
                        className="h-8 w-[190px] justify-between text-xs font-normal"
                      />
                    </TableCell>
                  </TableRow>
                  {expandido && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="border-l-4 border-l-sky-400 bg-sky-50/60 p-0"
                      >
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10" />
                              <TableHead>Documento</TableHead>
                              <TableHead>Vencimiento</TableHead>
                              <TableHead>Bucket</TableHead>
                              <TableHead className="text-right">Monto a pagar</TableHead>
                              <TableHead className="text-right">Monto DL</TableHead>
                              <TableHead>Cuenta bancaria</TableHead>
                              <TableHead className="w-10" />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {g.facturas.map((f) => {
                              const linea = incluidas.get(f.entryNo);
                              return (
                                <TableRow
                                  key={f.entryNo}
                                  className={cn(linea && 'bg-sky-100/70 hover:bg-sky-100')}
                                >
                                  <TableCell>
                                    <input
                                      type="checkbox"
                                      checked={!!linea}
                                      onChange={() => alternarFactura(f)}
                                      className="h-4 w-4 accent-adelante-primary"
                                    />
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">
                                    {f.documentNo ?? '—'}
                                    {etiquetas?.[f.entryNo] && (
                                      <span
                                        title={etiquetas[f.entryNo]?.titulo}
                                        className={cn(
                                          'ml-2 rounded px-1.5 py-0.5 font-sans text-[10px] font-medium',
                                          etiquetas[f.entryNo]?.tono === 'rojo'
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-amber-100 text-amber-800',
                                        )}
                                      >
                                        {etiquetas[f.entryNo]?.texto}
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {f.dueDate ? <FechaCR fecha={f.dueDate} /> : '—'}
                                  </TableCell>
                                  <TableCell>
                                    <BucketVencimientoBadge bucket={f.bucketVencimiento} />
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {linea ? (
                                      <div className="flex flex-col items-end gap-1">
                                        <div className="relative">
                                          <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                            {f.currencyCode === 'USD' ? '$' : '₡'}
                                          </span>
                                          <Input
                                            type="number"
                                            value={linea.montoAPagar}
                                            max={linea.montoMaximo}
                                            min={0}
                                            step="0.01"
                                            onChange={(e) =>
                                              fijarMonto(f.entryNo, Number(e.target.value))
                                            }
                                            className="h-8 w-32 pl-5 text-right"
                                          />
                                        </div>
                                        <div className="flex gap-1">
                                          {PORCENTAJES_RAPIDOS.map((pct) => (
                                            <button
                                              key={pct}
                                              type="button"
                                              onClick={() =>
                                                fijarMonto(
                                                  f.entryNo,
                                                  Math.round(
                                                    linea.montoMaximo * (pct / 100) * 100,
                                                  ) / 100,
                                                )
                                              }
                                              className={cn(
                                                'rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-accent',
                                                Math.round(linea.montoAPagar * 100) ===
                                                  Math.round(
                                                    linea.montoMaximo * (pct / 100) * 100,
                                                  ) &&
                                                  'border-adelante-primary text-adelante-primary-dark',
                                              )}
                                            >
                                              {pct}%
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-muted-foreground">
                                        {formatearMonto(
                                          f.remainingAmount ?? f.remainingAmtLCY ?? 0,
                                          f.currencyCode,
                                        )}
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right text-muted-foreground">
                                    <MontoCRC
                                      monto={linea?.montoAPagarLCY ?? f.remainingAmtLCY ?? 0}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {linea && (
                                      <BankAccountPickerBC
                                        value={linea.cuentaBancoOrigen}
                                        onChange={(cuenta) =>
                                          aplicarCuentaFactura(f.entryNo, cuenta)
                                        }
                                        soloMoneda={f.currencyCode || 'CRC'}
                                        className="h-8 w-[190px] justify-between text-xs font-normal"
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {f.linkBC && (
                                      <a
                                        href={f.linkBC}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="Ver en Business Central"
                                        className="text-muted-foreground hover:text-foreground"
                                      >
                                        <ArrowSquareOut
                                          className="h-4 w-4"
                                          weight="bold"
                                          aria-hidden="true"
                                        />
                                      </a>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
